import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translate } from './translate';
import { config } from '../config/runtimeConfig';
import {
  DEFAULT_LANGUAGE,
  LANDING_LANGUAGES,
  LANDING_LANGUAGE_META,
  getSelectedLanguage,
  hostPageLanguage,
  resolveInitialLanguage,
  setSelectedLanguage,
  subscribeToLanguage,
} from './selectedLanguage';

/**
 * b2b-demo i18n — минимальная инфра под Autodis salon-flow (issues #1022 / #1024).
 *
 * Источник локалей — `apps/b2b-ui/src/assets/i18n/{lang}.json`, синканный
 * скриптом `scripts/sync-i18n-translations.js` в `apps/b2b-demo/src/i18n/`.
 * Single source of truth с админкой; локально для b2b-demo это просто JSON-импорты.
 *
 * Выбор языка (#1362) живёт в `./selectedLanguage` — отдельном модуле, потому
 * что читать его нужно и вне React-дерева (чат-виджет в своём Shadow DOM без
 * провайдера). Порядок: явный выбор покупателя (module-level + localStorage)
 * → `fr`. `navigator.language`-автодетекта больше нет — см. `DEFAULT_LANGUAGE`.
 *
 * Поддерживаемые языки runtime: fr/en/de/lu (whitelist того же print template).
 * RU/EL имеют `print` namespace для админки, но в landing-flow пока не
 * показываем.
 */

// Whitelist и его лейблы живут вместе в `./selectedLanguage` — раздели их по
// файлам, и `LanguageSelector` (`languageMeta[lang].title`, без фоллбэка)
// упадёт на первом же языке, добавленном только в один из двух списков.
// Реэкспорт — для существующих импортов из этого модуля.
export { LANDING_LANGUAGES, LANDING_LANGUAGE_META };

const I18nContext = createContext(null);

/**
 * `translateInventory` — переводить ли ДАННЫЕ инвентаря (кузов, топливо,
 * опции) в язык покупателя (#1417). Включает только витрина (`App.jsx`).
 * Провайдер монтируется и внутри дилерского бандла (`registry/Catalog` —
 * `/showroom`, `/cars/:id/landing`; `WidgetViewOverlays`), и там карточки
 * обязаны показывать значения как раньше: наличие провайдера витрину не
 * означает.
 *
 * Язык, пока покупатель ничего не выбрал (#1469):
 *  - `defaultLanguage` — язык самого экрана, если он свёрстан на одном языке
 *    (лендинг Andys — `en`);
 *  - иначе, когда виджет встроен на сайт дилера, — язык этого сайта
 *    (`<html lang>`, см. `hostPageLanguage`);
 *  - иначе витринный `DEFAULT_LANGUAGE`.
 */
export const I18nProvider = ({ children, translateInventory = false, defaultLanguage }) => {
  const [language, setLanguageState] = useState(() =>
    resolveInitialLanguage(
      defaultLanguage ?? (config.ENVIRONMENT_NAME === 'widget' ? hostPageLanguage() : null)
    )
  );

  // Провайдеров может быть несколько одновременно (витрина в `registry/Catalog`
  // + mini-car overlay в `WidgetViewOverlays`), поэтому язык они берут не только
  // из своего состояния: выбор в одном должен обновить все.
  useEffect(() => subscribeToLanguage(setLanguageState), []);

  // Персиста тут намеренно НЕТ: writeback делает только `setLanguage` ниже.
  // Раньше эффект писал язык в localStorage на каждом монтировании, из-за чего
  // дефолт провайдера становился неотличим от осознанного выбора покупателя —
  // а виджет шлёт агенту именно выбор (#1362).
  //
  // `<html lang>` переписываем ТОЛЬКО по явному выбору. В embed'е это документ
  // ДИЛЕРА, а не наш: навязать немецкой странице `lang="fr"` лишь потому, что
  // покупатель не трогал селектор, — это регрессия для скринридера на чужой
  // странице. Раньше сюда попадал автодетект, который обычно совпадал с host.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!getSelectedLanguage()) return;
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next) => {
    const applied = setSelectedLanguage(next);
    if (!applied) {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] Ignoring unsupported language "${next}". Whitelist: ${LANDING_LANGUAGES.join(', ')}`);
      }
      return;
    }
    setLanguageState(applied);
  }, []);

  const t = useCallback((key, fallback) => {
    const value = translate(language, key);
    if (value !== undefined) return value;
    if (import.meta.env.DEV) {
      console.warn(`[i18n] Missing key "${key}" in lang=${language} (no FR/EN fallback either).`);
    }
    return fallback !== undefined ? fallback : key;
  }, [language]);

  const value = useMemo(() => ({
    translateInventory,
    language,
    setLanguage,
    languages: LANDING_LANGUAGES,
    languageMeta: LANDING_LANGUAGE_META,
    t,
  }), [translateInventory, language, setLanguage, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/**
 * Контекст провайдера или `null`, если провайдера сверху нет. В отличие от
 * `useTranslation()` (который вне провайдера отдаёт шим с `fr`) позволяет
 * отличить «язык страницы» от «провайдера нет вовсе» (#1440).
 */
export const useOptionalI18n = () => useContext(I18nContext);

/**
 * Хук — если вызван вне `<I18nProvider>`, возвращает fallback-shim
 * (язык 'fr', `t()` возвращает ключ или fallback). Это удерживает компоненты,
 * которые **могут** монтироваться без провайдера (legacy print-flow в standalone)
 * от runtime ошибки.
 */
export const useTranslation = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return {
      // #1417: без провайдера язык — лишь дефолт, а не выбор покупателя;
      // данные инвентаря не переводим (см. `translateInventory`).
      translateInventory: false,
      language: DEFAULT_LANGUAGE,
      setLanguage: () => {},
      languages: LANDING_LANGUAGES,
      languageMeta: LANDING_LANGUAGE_META,
      t: (key, fallback) => fallback !== undefined ? fallback : key,
    };
  }
  return ctx;
};
